import React from 'react';
import { Header, Icon, Segment } from 'semantic-ui-react';

import { clienteApi } from 'src/api';
import { Contexto } from 'src/contexto';
import {
    buscarPalabras,
    colapsarEspacios,
    nombreCompletoConNombresPrimero,
    sanearTildes,
} from 'src/utilidades';
import { MenuSuperior } from './MenuSuperior';
import { Tabla } from './Tabla';
import { DatosDeContactoDeAlumno, FiltroArea, FiltroAño } from './tipos';

type Props = Readonly<{
    ocultar: boolean;
}>;

type State = Readonly<{
    buscandoMatriculas: boolean;
    matriculas: ReadonlyArray<DatosDeContactoDeAlumno>;
    matriculasFiltradas: ReadonlyArray<DatosDeContactoDeAlumno>;
    textoBusqueda: string;
    filtroArea: FiltroArea;
    filtroAño: FiltroAño;
    error?: {
        readonly mensaje: string;
        readonly detalles: ReadonlyArray<string>;
    };
}>;

class ListaGeneral extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = {
        buscandoMatriculas: false,
        matriculas: [],
        matriculasFiltradas: [],
        textoBusqueda: '',
        filtroArea: 'todas',
        filtroAño: 2021,
    };

    public componentDidMount() {
        this.obtenerDatosDeContacto();
    }

    public componentDidUpdate(prevProps: Props) {
        if (!this.props.ocultar && prevProps.ocultar) {
            this.obtenerDatosDeContacto();
        }
    }

    public render() {
        const {
            buscandoMatriculas,
            matriculasFiltradas,
            filtroArea,
            filtroAño,
            textoBusqueda,
        } = this.state;

        const matriculas = textoBusqueda.length >= 3 ? matriculasFiltradas : [];

        return (
            <div hidden={this.props.ocultar}>
                <MenuSuperior
                    cargando={buscandoMatriculas}
                    matriculas={matriculas}
                    filtroArea={filtroArea}
                    filtroAño={filtroAño}
                    alCambiarFiltroDeArea={this.establecerFiltroArea}
                    alCambiarFiltroDeAño={this.establecerFiltroAño}
                    alCambiarTextoDeBusqueda={this.filtrarFilas}
                    alPresionarRecargar={this.obtenerDatosDeContacto}
                />

                {textoBusqueda.length >= 3 ? (
                    <Tabla matriculas={matriculas} />
                ) : (
                    <Segment attached='bottom' placeholder>
                        <Header icon size='small'>
                            <Icon name='search' />
                            Escriba al menos 3 caracteres en el cuadro de búsqueda para comenzar a
                            buscar.
                        </Header>
                    </Segment>
                )}
            </div>
        );
    }

    private readonly filtrarFilas = (criterio: string, { area = this.state.filtroArea } = {}) => {
        const { matriculas } = this.state;
        const textoBusqueda = sanearTildes(colapsarEspacios(criterio)).toLowerCase();

        const filtradas = matriculas.filter(mat => satisfaceFiltroArea(mat, area));

        const encontradas =
            textoBusqueda !== ''
                ? filtradas.filter(
                      fila =>
                          buscarEnNombrePersonal(textoBusqueda, fila.nombreAlumno) ||
                          normalizarRut(fila.rutPasaporteAlumno).startsWith(
                              normalizarRut(textoBusqueda),
                          ) ||
                          fila.correosAlumno.some(correo =>
                              correo.toLowerCase().includes(textoBusqueda),
                          ) ||
                          fila.numeroDeContrato.toString().startsWith(textoBusqueda) ||
                          buscarEnNombrePersonal(textoBusqueda, fila.nombreApoderado),
                  )
                : filtradas;

        this.setState({ matriculasFiltradas: encontradas, textoBusqueda });
    };

    private readonly establecerFiltroArea = (filtroArea: FiltroArea) => {
        this.setState({ filtroArea });
        this.filtrarFilas(this.state.textoBusqueda, { area: filtroArea });
    };

    private readonly establecerFiltroAño = (filtroAño: FiltroAño) => {
        this.setState({ filtroAño }, () => this.obtenerDatosDeContacto());
    };

    private readonly obtenerDatosDeContacto = () => {
        this.setState({ buscandoMatriculas: true });

        const { filtroAño } = this.state;

        clienteApi
            .ejecutar('datosDeContactoDeAlumnos', {
                año: filtroAño === 'todos' ? undefined : filtroAño,
            })
            .then(resultados => {
                const matriculas = resultados.sort((a, b) =>
                    nombreCompletoConNombresPrimero(a.nombreAlumno).localeCompare(
                        nombreCompletoConNombresPrimero(b.nombreAlumno),
                    ),
                );
                this.setState(
                    {
                        buscandoMatriculas: false,
                        matriculas,
                    },
                    () => {
                        this.filtrarFilas(this.state.textoBusqueda);
                    },
                );
            })
            .catch(this.context.mostrarModalError);
    };
}

interface TNombrePersonal {
    primerNombre: string;
    nombresSecundarios: string;
    apellidoPaterno: string;
    apellidoMaterno: string;
}

function buscarEnNombrePersonal(
    texto: string,
    nombrePersonal: TNombrePersonal,
    incluirSolo?: ReadonlyArray<keyof TNombrePersonal>,
): boolean {
    const partes = incluirSolo || [
        'primerNombre',
        'nombresSecundarios',
        'apellidoPaterno',
        'apellidoMaterno',
    ];

    // Lo más simple pero menos eficiente: concatena el nombre en base a las partes dadas
    // y luego busca las palabras. buscarPalabras volverá a separar el texto en palabras.
    // Si se cambia esta función para no juntar el nombre (para volverlo a separar) se debe
    // considerar que cada parte del nombre puede tener varias palabras.
    const nombre = partes.reduce((n, parte) => n + ' ' + nombrePersonal[parte], '');
    return buscarPalabras(sanearTildes(nombre).toLocaleLowerCase(), texto);
}

function satisfaceFiltroArea(fila: Readonly<DatosDeContactoDeAlumno>, filtro: FiltroArea) {
    if (filtro === 'todas') return true;
    return fila.area === filtro;
}

export default ListaGeneral;

function normalizarRut(rut: string) {
    return rut.replace(/\./g, '').toLowerCase();
}



// WEBPACK FOOTER //
// ./src/modulos/Alumnos/paginas/ListaGeneral/index.tsx